import { type IntlShape } from "react-intl";

import { messages } from "../components/messages.generated";
import { type SubmissionCheck, type SubmissionCheckState } from "../model";
import { type WebLensBuildOutputMeasurement } from "../utils/measureWebLensBuildOutput";

import {
    buildBudgetCheck,
    buildLogoChecks,
    buildPackageSizeChecks,
    describePackageTypeFailure,
    isZipPackage,
} from "./preChecks";

interface SubmissionReadiness {
    file: File | null;
    logo: File | null;
    name: string;
    hasVideoPreview: boolean;
    isPreviewTouched: boolean;
    /** Measured build output of the picked package; null while unmeasured (row absent). */
    buildOutput: WebLensBuildOutputMeasurement | null;
}

/**
 * A row is neutral until the creator has acted on it, so an untouched form reads as a to-do list
 * rather than as a list of errors. `started` is what makes an unmet row an actual problem: for the
 * package rows that is a file being attached, and for preview rows it is preview interaction.
 */
export const resolveReadinessState = (satisfied: boolean, started: boolean): SubmissionCheckState => {
    if (satisfied) {
        return "pass";
    }
    return started ? "fail" : "pending";
};

/**
 * The submission rail's live readiness list: what still stands between the creator and a valid
 * submission, recomputed from form state on every render.
 *
 * Distinct from `runClientPreChecks`, which runs once inside `submit()` and reports the progress of
 * a submission already under way. Both describe the package with the same predicates, so the rail
 * never promises a file that submit() then rejects.
 */
export const getReadinessChecks = (
    intl: IntlShape,
    { file, logo, name, hasVideoPreview, isPreviewTouched, buildOutput }: SubmissionReadiness,
): SubmissionCheck[] => {
    const hasFile = Boolean(file);

    return [
        {
            id: "package-type",
            label: intl.formatMessage(messages.checkPackageTypeLabel),
            state: resolveReadinessState(Boolean(file && isZipPackage(file)), hasFile),
            detail: file ? describePackageTypeFailure(intl, file) : undefined,
        },
        ...(file ? buildPackageSizeChecks(intl, file) : []),
        // Positioned to match runClientPreChecks' order, so the rows a creator is already reading
        // keep their places when submit() takes the rail over.
        ...(buildOutput ? [buildBudgetCheck(intl, buildOutput)] : []),
        ...(logo ? buildLogoChecks(intl, logo) : []),
        {
            id: "name",
            // Never a failure: an empty name is something still to do, not something done wrong.
            label: intl.formatMessage(messages.checkNameLabel),
            state: resolveReadinessState(name.trim().length > 0, false),
        },
        {
            id: "preview",
            label: intl.formatMessage(messages.checkPreviewLabel),
            state: resolveReadinessState(hasVideoPreview, isPreviewTouched),
            detail:
                !hasVideoPreview && isPreviewTouched
                    ? intl.formatMessage(messages.webLensPreviewRequirement)
                    : undefined,
        },
    ];
};
